import React, { useEffect, useRef } from "react";
import { useI18n } from "@/i18n/hooks";
import { Button } from "../Base/Button";
import { useUserDataFlattened } from "../../modules";
import {
  usePlatformLogo,
  useOnlineServiceSupportUrl,
} from "../../modules/homeDisplay/hooks";
import { useNavigation, ROUTES } from "../../utils/navigation";
import { openLoginRegisterPopup } from "../LoginRegisterPopup";
import { useMessageNotification } from "@/modules/notification";
import "./Header.scss";
import type { HeaderProps } from "./Header.types";
import { utils } from "../..";
import IconSearch from "@/assets/icons/ic-search.svg";
import IconSupport from "@/assets/icons/support-icon.png";
import IconMessage from "@/assets/icons/message.png";
import IconLanguage from "@/assets/icons/ic-language.svg";

const ICON_URLS = {
  search: IconSearch,
  service: IconSupport,
  message: IconMessage,
  language: IconLanguage,
};

export const Header: React.FC<HeaderProps> = ({
  className = "",
  pageType = "home",
  onLanguageClick,
  onMessageClick,
  onServiceClick,
  onSearchClick,
  messageUnreadCount: messageUnreadCountProp,
}) => {
  const { t } = useI18n();
  const { navigate } = useNavigation();
  const { isAuthenticated } = useUserDataFlattened();
  const headerRef = useRef<HTMLElement>(null);
  const onlineServiceSupportUrl = useOnlineServiceSupportUrl();
  const { messageUnreadCount: notificationUnreadCount } =
    useMessageNotification();
  const messageUnreadCount =
    messageUnreadCountProp ?? (pageType === "profile" ? notificationUnreadCount : 0);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const sync = () => {
      const h = el.getBoundingClientRect().height;
      document.documentElement.style.setProperty("--header-height", `${h}px`);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const platformLogo = usePlatformLogo();
  const logoSrc = utils.getImageUrl(platformLogo?.logoUrl || "");

  const handleServiceClick = () => {
    if (onServiceClick) {
      onServiceClick();
    } else if (onlineServiceSupportUrl) {
      window.location.href = onlineServiceSupportUrl;
    } else {
      navigate(`${ROUTES.MESSAGE_CENTER}?tabIndex=1`);
    }
  };

  const handleSearchClick = () => {
    if (onSearchClick) {
      onSearchClick();
    } else {
      navigate("/game-search");
    }
  };

  const handleMessageClick = () => {
    if (onMessageClick) {
      onMessageClick();
    } else {
      navigate(`${ROUTES.MESSAGE_CENTER}?tabIndex=2`);
    }
  };

  const renderIcons = () => {
    if (isAuthenticated) {
      switch (pageType) {
        case "profile":
          return (
            <div className="mobile-header__right-icons">
              {/* <button
                className="mobile-header__language-icon"
                onClick={onLanguageClick}
                aria-label={t("profile.language")}
              >
                <img
                  src={ICON_URLS.language}
                  alt=""
                  className="mobile-header__auth-bar-icon-img"
                  draggable={false}
                />
              </button> */}
              <button
                className="mobile-header__message-icon"
                onClick={handleMessageClick}
                aria-label={t("common.messageCenter")}
              >
                <img
                  src={ICON_URLS.message}
                  alt=""
                  className="mobile-header__auth-bar-icon-img"
                  draggable={false}
                />
                {messageUnreadCount > 0 && (
                  <span className="mobile-header__badge">
                    {messageUnreadCount > 99 ? "99+" : messageUnreadCount}
                  </span>
                )}
              </button>
              <button
                className="mobile-header__service-icon"
                onClick={handleServiceClick}
                aria-label={t("profile.support")}
              >
                <img
                  src={ICON_URLS.service}
                  alt=""
                  className="mobile-header__auth-bar-icon-img"
                  draggable={false}
                />
              </button>
            </div>
          );
        case "vip":
          return (
            <div className="mobile-header__right-icons">
              <button
                className="mobile-header__service-icon"
                onClick={handleServiceClick}
                aria-label={t("profile.support")}
              >
                <img
                  src={ICON_URLS.service}
                  alt=""
                  className="mobile-header__auth-bar-icon-img"
                  draggable={false}
                />
              </button>
            </div>
          );
        case "home":
        default:
          return (
            <div className="mobile-header__right-icons">
              <button
                className="mobile-header__search-icon"
                onClick={handleSearchClick}
                aria-label="Search"
              >
                <img
                  src={ICON_URLS.search}
                  alt=""
                  className="mobile-header__auth-bar-icon-img"
                  draggable={false}
                />
              </button>
              <button
                className="mobile-header__service-icon"
                onClick={handleServiceClick}
                aria-label={t("profile.support")}
              >
                <img
                  src={ICON_URLS.service}
                  alt=""
                  className="mobile-header__auth-bar-icon-img"
                  draggable={false}
                />
              </button>
            </div>
          );
      }
    }

    // 未登录状态
    if (pageType === "profile") {
      // profile 页面未登录时：不显示登录按钮，显示语言、消息、客服 icon
      return (
        <div className="mobile-header__right-icons">
          <button
            className="mobile-header__language-icon"
            onClick={onLanguageClick}
            aria-label={t("profile.language")}
          >
            <img
              src={ICON_URLS.language}
              alt=""
              className="mobile-header__auth-bar-icon-img"
              draggable={false}
            />
          </button>
          <button
            className="mobile-header__message-icon"
            onClick={handleMessageClick}
            aria-label={t("common.messageCenter")}
          >
            <img
              src={ICON_URLS.message}
              alt=""
              className="mobile-header__auth-bar-icon-img"
              draggable={false}
            />
          </button>
          <button
            className="mobile-header__service-icon"
            onClick={handleServiceClick}
            aria-label={t("profile.support")}
          >
            <img
              src={ICON_URLS.service}
              alt=""
              className="mobile-header__auth-bar-icon-img"
              draggable={false}
            />
          </button>
        </div>
      );
    }

    // 其他页面未登录时：显示登录按钮 + 搜索、客服 icon
    return (
      <>
        <Button
          variant="primary"
          size="sm"
          className="mobile-header__register-login-btn"
          onClick={() => openLoginRegisterPopup({ defaultMode: "register" })}
        >
          {t("auth.registerLogin")}
        </Button>
        <div className="mobile-header__right-icons">
          <button
            className="mobile-header__search-icon"
            onClick={handleSearchClick}
            aria-label="Search"
          >
            <img
              src={ICON_URLS.search}
              alt=""
              className="mobile-header__auth-bar-icon-img"
              draggable={false}
            />
          </button>
          <button
            className="mobile-header__service-icon"
            onClick={handleServiceClick}
            aria-label={t("profile.support")}
          >
            <img
              src={ICON_URLS.service}
              alt=""
              className="mobile-header__auth-bar-icon-img"
              draggable={false}
            />
          </button>
        </div>
      </>
    );
  };

  return (
    <header
      ref={headerRef}
      className={`mobile-header ${isAuthenticated ? "mobile-header--authenticated" : ""} ${className}`}
    >
      <div className="mobile-header__content">
        <div className="mobile-header__auth-bar">
          <div className="mobile-header__logo">
            {logoSrc && (
              <img
                src={logoSrc}
                alt="Logo"
                className="mobile-header__logo-img"
                draggable={false}
              />
            )}
          </div>

          <div className="mobile-header__right-section">{renderIcons()}</div>
        </div>
      </div>
    </header>
  );
};

export default Header;
